import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Copy, Download, Printer } from "lucide-react";
import { downloadTextFile } from "@/lib/csv";
import { runExport, type ExportDeps, type ExportKind } from "@/lib/calc/exportActions";

/**
 * Copy, CSV and Print for a calculator. `gate` is the tool's own gate (useLogToolUsage): each action is one trial use
 * per visit, taken only when the output is really handed over, and a blocked action opens the paywall and delivers nothing.
 */
export function ExportBar({ gate, text, csv, filename, label = "table" }: {
  gate: () => Promise<boolean>; text: string; csv: string; filename: string; label?: string;
}) {
  const deps: ExportDeps = {
    gate,
    copyText: async (t) => { await navigator.clipboard.writeText(t); },
    saveCsv: (name, c) => downloadTextFile(name, c),
    print: () => window.print(),
  };
  const go = async (kind: ExportKind) => {
    const ok = await runExport(kind, deps, { text, csv, filename });
    if (ok && kind === "copy") toast.success(`${label[0].toUpperCase()}${label.slice(1)} copied`);
    if (!ok && kind === "copy") toast.error("Couldn't copy. Use Download CSV instead.");
  };
  return (
    <div className="no-print print:hidden flex flex-wrap gap-2" role="group" aria-label={`Export the ${label}`} data-testid="calc-export-bar">
      <Button type="button" size="sm" variant="outline" className="gap-1.5" onClick={() => void go("copy")}><Copy className="h-4 w-4" aria-hidden="true" />Copy {label}</Button>
      <Button type="button" size="sm" variant="outline" className="gap-1.5" onClick={() => void go("csv")}><Download className="h-4 w-4" aria-hidden="true" />Download CSV</Button>
      <Button type="button" size="sm" variant="outline" className="gap-1.5" onClick={() => void go("print")}><Printer className="h-4 w-4" aria-hidden="true" />Print or save as PDF</Button>
    </div>
  );
}
